import React, { useState } from 'react';
import { Grid3x3, Sun, Moon, X, Copy, Check, Box } from 'lucide-react';
import { spacing, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from '../ui/Drawer';
import ProjectsDrawer from '../ui/ProjectsDrawer';

/**
 * LeftSidebar Component
 *
 * Left sidebar with title, description, character library popover, and footer.
 */
const LeftSidebar = ({ characterSet, onCharacterSetChange }) => {
  const { colors, toggleTheme, isDark } = useTheme();
  const [copiedPreset, setCopiedPreset] = useState(null);
  const [isProjectsDrawerOpen, setIsProjectsDrawerOpen] = useState(false);

  const characterPresets = [
    { name: 'Default', chars: '█▓▒░·∙˙¨ ' },
    { name: 'Simple', chars: '█▓▒░ ' },
    { name: 'Minimal', chars: '█░ ' },
    { name: 'Dense', chars: '█▓▒░@#%&*+=-:. ' },
    { name: 'ASCII Classic', chars: '@%#*+=-:. ' }
  ];

  const handleCopyCharacters = (preset, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(preset.chars);
    setCopiedPreset(preset.name);
    setTimeout(() => setCopiedPreset(null), 2000);
  };

  return (
    <div style={{
      width: '280px',
      minWidth: '280px',
      maxWidth: '280px',
      flexShrink: 0,
      background: colors.background,
      padding: spacing.xxl,
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.xl,
      borderRight: `1px solid ${colors.border}`,
      overflowY: 'auto'
    }}>
      <div>
        <h1 style={{
          margin: 0,
          fontSize: '1.5rem',
          fontWeight: 600,
          color: colors.foreground,
          marginBottom: spacing.xs
        }}>
          Significa ASCII
        </h1>
        <p style={{
          margin: 0,
          fontSize: '1rem',
          fontWeight: 500,
          color: colors.foregroundSecondary,
          letterSpacing: '0.025em'
        }}>
          Create your own ASCII patterns
        </p>
      </div>

      <div style={{
        fontSize: '0.875rem',
        lineHeight: '1.6',
        color: colors.foregroundSecondary,
        fontWeight: 400
      }}>
        <p style={{ margin: 0 }}>
          This system generates ASCII patterns moving through our process: Think, Design, Develop, Launch, and Scale. Submit any prompt and each set of characters will somehow incorporate an egg, the symbol at the base of our logo. These visual representations of all things Significa support our brand comms, but it's open for you to try and play around with, too.
        </p>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: 'auto',
        paddingTop: spacing.xl,
        fontSize: '0.75rem',
        color: colors.foregroundSecondary,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.lg
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.lg
        }}>
          {/* Theme Toggle */}
          <div style={{
            display: 'inline-flex',
            width: 'fit-content',
            background: isDark ? 'rgba(33, 33, 33, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            borderRadius: '4px',
            padding: '2px',
            border: `1px solid ${colors.border}`
          }}>
            {[
              { mode: 'light', icon: <Sun size={12} /> },
              { mode: 'dark', icon: <Moon size={12} /> }
            ].map(({ mode, icon }) => (
              <button
                key={mode}
                onClick={() => {
                  if ((mode === 'dark' && !isDark) || (mode === 'light' && isDark)) {
                    toggleTheme();
                  }
                }}
                style={{
                  padding: '4px',
                  color: (mode === 'dark' && isDark) || (mode === 'light' && !isDark)
                    ? colors.background
                    : colors.foregroundSecondary,
                  background: (mode === 'dark' && isDark) || (mode === 'light' && !isDark)
                    ? colors.foreground
                    : 'transparent',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  outline: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  const isActive = (mode === 'dark' && isDark) || (mode === 'light' && !isDark);
                  if (!isActive) {
                    e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  const isActive = (mode === 'dark' && isDark) || (mode === 'light' && !isDark);
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {icon}
              </button>
            ))}
          </div>

          <Drawer>
            <DrawerTrigger asChild>
              <div style={{
                display: 'inline-flex',
                width: 'fit-content',
                background: colors.backgroundOffset,
                borderRadius: borderRadius.sm,
                padding: '6px 10px',
                border: `1px solid ${colors.border}`,
                transition: 'all 0.2s',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.background;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.backgroundOffset;
              }}>
                <button style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: 0,
                  background: 'none',
                  border: 'none',
                  color: colors.foreground,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}>
                  <Grid3x3 size={14} />
                  Character Library
                </button>
              </div>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingBottom: spacing.sm, paddingLeft: spacing.xl, paddingRight: spacing.xl }}>
                <DrawerTitle>Character Library</DrawerTitle>
                <DrawerClose asChild>
                  <button style={{
                    position: 'absolute',
                    right: spacing.xl,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    color: colors.foregroundSecondary,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = colors.backgroundOffset;
                    e.currentTarget.style.color = colors.foreground;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = colors.foregroundSecondary;
                  }}
                  >
                    <X size={16} />
                  </button>
                </DrawerClose>
              </DrawerHeader>
              <DrawerDescription>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.md
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '0.875rem',
                    color: colors.foregroundSecondary,
                    lineHeight: 1.5
                  }}>
                    Choose a character set to customize your ASCII art appearance.
                  </p>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: spacing.md
                  }}>
                    {characterPresets.map(preset => (
                      <div
                        key={preset.name}
                        style={{
                          position: 'relative',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: spacing.sm,
                          padding: spacing.md,
                          background: colors.backgroundOffset,
                          border: `1px solid ${colors.border}`,
                          borderRadius: borderRadius.md,
                          fontSize: '0.875rem',
                          transition: 'all 0.2s',
                          color: colors.foreground,
                          textAlign: 'left'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{ fontWeight: 600 }}>{preset.name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                            <span style={{
                              fontSize: '0.75rem',
                              color: colors.foregroundSecondary
                            }}>
                              {preset.chars.length} chars
                            </span>
                            <button
                              onClick={(e) => handleCopyCharacters(preset, e)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px',
                                padding: '4px',
                                background: colors.background,
                                border: `1px solid ${colors.border}`,
                                borderRadius: borderRadius.sm,
                                color: copiedPreset === preset.name ? colors.foreground : colors.foregroundSecondary,
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = colors.backgroundOffset;
                                e.currentTarget.style.color = colors.foreground;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = colors.background;
                                e.currentTarget.style.color = copiedPreset === preset.name ? colors.foreground : colors.foregroundSecondary;
                              }}
                            >
                              {copiedPreset === preset.name ? <Check size={12} /> : <Copy size={12} />}
                            </button>
                          </div>
                        </div>

                        <button
                          onClick={() => {
                            onCharacterSetChange && onCharacterSetChange(preset.chars);
                          }}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(32px, 1fr))',
                            gap: spacing.xs,
                            padding: spacing.sm,
                            background: colors.background,
                            borderRadius: borderRadius.sm,
                            border: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = colors.background;
                          }}
                        >
                          {preset.chars.split('').map((char, i) => (
                            <div
                              key={i}
                              style={{
                                fontFamily: 'monospace',
                                fontSize: '1.25rem',
                                textAlign: 'center',
                                color: colors.foreground,
                                padding: spacing.xs,
                                background: colors.backgroundOffset,
                                borderRadius: borderRadius.xs
                              }}
                            >
                              {char === ' ' ? '␣' : char}
                            </div>
                          ))}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </DrawerDescription>
            </DrawerContent>
          </Drawer>

          <div
            onClick={() => setIsProjectsDrawerOpen(true)}
            style={{
              display: 'inline-flex',
              width: 'fit-content',
              background: colors.backgroundOffset,
              borderRadius: borderRadius.sm,
              padding: '6px 10px',
              border: `1px solid ${colors.border}`,
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = colors.background;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = colors.backgroundOffset;
            }}>
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: 0,
              background: 'none',
              border: 'none',
              color: colors.foreground,
              fontSize: '0.75rem',
              fontWeight: 500,
              cursor: 'pointer'
            }}>
              <Box size={14} />
              More from Significa
            </button>
          </div>

          <div style={{
            display: 'inline-flex',
            width: 'fit-content',
            alignItems: 'center',
            gap: spacing.xs,
            padding: '4px 8px',
            background: colors.backgroundOffset,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.sm
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: '#10b981',
              boxShadow: '0 0 12px rgba(16, 185, 129, 0.8), 0 0 6px rgba(16, 185, 129, 1)',
              animation: 'pulse-glow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
            }} />
            <span style={{
              color: colors.foregroundSecondary,
              fontSize: '0.75rem',
              fontWeight: 500
            }}>
              Recently updated
            </span>
            <style>
              {`
                @keyframes pulse-glow {
                  0%, 100% {
                    opacity: 1;
                    transform: scale(1);
                    box-shadow: 0 0 12px rgba(16, 185, 129, 0.8), 0 0 6px rgba(16, 185, 129, 1);
                  }
                  50% {
                    opacity: 0.4;
                    transform: scale(1.3);
                    box-shadow: 0 0 20px rgba(16, 185, 129, 0.6), 0 0 10px rgba(16, 185, 129, 0.8);
                  }
                }
              `}
            </style>
          </div>

          <div>
            Made with love by{' '}
            <a
              href="https://significa.co"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.foreground,
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.6'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Significa
            </a>
          </div>
        </div>
      </div>

      {/* Projects Drawer */}
      <ProjectsDrawer
        isOpen={isProjectsDrawerOpen}
        onClose={() => setIsProjectsDrawerOpen(false)}
      />
    </div>
  );
};

export default React.memo(LeftSidebar);
